import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FolderPlus, ChefHat, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExtraRecipes = () => {
  const [recipes, setRecipes] = useState([]);
  const [usedRecipes, setUsedRecipes] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [recipesRes, regularRotRes, specialRotRes] = await Promise.all([
        axios.get(`${API}/recipes`),
        axios.get(`${API}/rotations/regular`),
        axios.get(`${API}/rotations/special`)
      ]);
      
      // Collect all used recipe names from rotations
      const used = new Set();
      
      // Check regular rotation
      if (regularRotRes.data.length > 0) {
        const regularRot = regularRotRes.data[0];
        const schedule = regularRot.schedule || {};
        Object.values(schedule).forEach(week => {
          Object.values(week).forEach(day => {
            if (day.sabzi) used.add(day.sabzi);
            if (day.daal) used.add(day.daal);
            if (day.salad) used.add(day.salad);
          });
        });
      }
      
      // Check Special rotation
      if (specialRotRes.data.length > 0) {
        const specialRot = specialRotRes.data[0];
        const schedule = specialRot.schedule || {};
        Object.values(schedule).forEach(week => {
          // Handle both old format (week.main) and new format (week.Sunday.main)
          if (week.main) {
            used.add(week.main);
            if (week.roti) used.add(week.roti);
            if (week.raita) used.add(week.raita);
            if (week.side) used.add(week.side);
          } else {
            Object.values(week).forEach(day => {
              if (day.main) used.add(day.main);
              if (day.roti) used.add(day.roti);
              if (day.raita) used.add(day.raita);
              if (day.side) used.add(day.side);
            });
          }
        });
      }
      
      setUsedRecipes(used);
      
      // Filter to only show recipes NOT used in any rotation
      const unusedRecipes = recipesRes.data.filter(r => 
        r.active && !used.has(r.recipe_name)
      );
      setRecipes(unusedRecipes);
      
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      Daal: 'border-l-orange-500 bg-orange-50',
      Sabzi: 'border-l-green-500 bg-green-50',
      Salad: 'border-l-cyan-500 bg-cyan-50',
      SpecialMain: 'border-l-[#faa023] bg-[#faa023]/10',
      SpecialRoti: 'border-l-[#faa023] bg-[#faa023]/10',
      SpecialRaita: 'border-l-[#faa023] bg-[#faa023]/10',
      SpecialSide: 'border-l-[#faa023] bg-[#faa023]/10',
      Sweet: 'border-l-pink-500 bg-pink-50',
      Other: 'border-l-stone-400 bg-stone-50'
    };
    return colors[category] || colors.Other;
  };

  // Group recipes by category
  const groupedRecipes = recipes.reduce((acc, recipe) => {
    const category = recipe.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(recipe);
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="extra-recipes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-800 tracking-tight flex items-center gap-3">
            <FolderPlus className="text-[#124242]" size={36} />
            Extra Recipes
          </h1>
          <p className="text-stone-500 mt-1">
            Recipes not yet used in any rotation
          </p>
        </div>
      </div>

      {/* Info Card */}
      <div className="virsa-card p-4 bg-[#124242]/5 border-[#124242]/20">
        <div className="flex items-start gap-3">
          <Info className="text-[#124242] flex-shrink-0 mt-0.5" size={20} />
          <div>
            <h3 className="font-semibold text-[#124242]">How Extra Recipes Work</h3>
            <ul className="text-sm text-stone-600 mt-2 space-y-1 list-disc list-inside">
              <li>This page shows all recipes that haven't been added to any rotation yet</li>
              <li>Once you use a recipe in Regular or Special rotation and save, it will be removed from here</li>
              <li>Used recipes show with a <span className="inline-block w-3 h-3 bg-yellow-400 rounded mx-1"></span> yellow indicator in the rotation pages</li>
              <li>Click on any recipe to view its details</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Recipes List */}
      {recipes.length > 0 ? (
        <div className="space-y-6">
          {Object.entries(groupedRecipes).map(([category, categoryRecipes]) => (
            <div key={category}>
              <h2 className="text-lg font-bold text-stone-700 mb-3 flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${
                  category === 'Sabzi' ? 'bg-green-500' :
                  category === 'Daal' ? 'bg-orange-500' :
                  category === 'Salad' ? 'bg-cyan-500' :
                  category.startsWith('Special') ? 'bg-[#faa023]' :
                  'bg-stone-400'
                }`}></span>
                {category}
                <span className="text-sm font-normal text-stone-500">({categoryRecipes.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryRecipes.map(recipe => (
                  <div
                    key={recipe.id}
                    className={`virsa-card border-l-4 ${getCategoryColor(recipe.category)} overflow-hidden cursor-pointer hover:shadow-lg transition-shadow`}
                    onClick={() => navigate(`/recipes/${recipe.id}`)}
                    data-testid={`extra-recipe-${recipe.id}`}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between gap-2 mb-3">
                        <div>
                          <h3 className="font-bold text-stone-800 text-lg">{recipe.recipe_name}</h3>
                          <div className="flex flex-wrap gap-2 mt-1">
                            <span className="badge badge-teal">{recipe.category}</span>
                            <span className="badge bg-stone-100 text-stone-600">Not in rotation</span>
                          </div>
                        </div>
                        <ChefHat size={20} className="text-stone-400 flex-shrink-0" />
                      </div>

                      <p className="text-sm text-stone-600">
                        {recipe.ingredients?.length || 0} ingredient{recipe.ingredients?.length !== 1 ? 's' : ''}
                      </p>

                      {recipe.ingredients?.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-stone-200">
                          <p className="text-xs text-stone-500 font-semibold uppercase mb-2">Ingredients (per tiffin)</p>
                          <div className="space-y-1 text-xs text-stone-600 max-h-24 overflow-y-auto">
                            {recipe.ingredients.slice(0, 5).map((ing, i) => (
                              <div key={`${ing.ingredient_name}-${ing.unit}`} className="flex justify-between">
                                <span>{ing.ingredient_name}</span>
                                <span className="text-stone-500">{ing.qty_per_tiffin} {ing.unit}</span>
                              </div>
                            ))}
                            {recipe.ingredients.length > 5 && (
                              <p className="text-stone-400 text-center">+{recipe.ingredients.length - 5} more</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="virsa-card p-12 text-center">
          <FolderPlus size={48} className="mx-auto text-stone-300 mb-4" />
          <h3 className="text-xl font-semibold text-stone-600 mb-2">All recipes are in use!</h3>
          <p className="text-stone-500">
            Every recipe has been added to at least one rotation. Great job!
          </p>
        </div>
      )}
    </div>
  );
};
